import { useCallback, createElement } from 'react';
import { SVGIcon } from '@loomstateorg/shared-frontend';
import { Variant } from '@commercetools/frontend-domain-types/product/Variant';
import { LineItem } from '@commercetools/frontend-domain-types/cart/LineItem';
import { IconText } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/greenland-score';
import { SustainabilityItem } from 'components/commercetools-ui/types';

const useImpacts = (): UseImpactsReturn => {
  const getImpactByProduct = useCallback(
    (
      variant: Variant,
      sustainability?: {
        cartEnvironmentalItem?: SustainabilityItem;
        certificationsItem?: SustainabilityItem;
        environmentalItem?: SustainabilityItem;
      },
    ) => {
      const attributes = variant?.attributes;

      const setIconData =
        (sustainability?.environmentalItem?.icons as any[])
          ?.map((icon: any) => {
            const key = icon?.key;
            const val = attributes?.[key];

            if (val === undefined || val === null) return;

            return {
              icon: createElement(SVGIcon, {
                src: icon?.file?.fields?.file?.url || '',
                alt: icon?.title || '',
                imgClassName: 'max-w-full max-h-full',
                className: 'h-full w-full',
              }),
              text: `${val || 0}`,
              units: icon?.unitOfMeasurement || '',
              label: icon?.title || '',
              attributeKey: key,
            };
          })
          .filter(Boolean) || [];

      return setIconData as IconText[];
    },
    [],
  );

  const getTotalImpactByCart = useCallback(
    (
      lineItems: LineItem[],
      sustainability?: {
        cartEnvironmentalItem?: SustainabilityItem;
        cartCertificationsItem?: SustainabilityItem;
      },
    ) => {
      const summaryImpacts: any = [];
      lineItems.forEach((lineItem) => {
        const count = lineItem?.count;
        const attributes = lineItem.variant?.attributes;
        const impactEntries = Object.entries(attributes || [])
          ?.filter(([key, val]) => key.startsWith('impacts'))
          ?.map(([key, val]) => [key, val * (count || 0)]);
        const impactsObject = Object.fromEntries(impactEntries);
        summaryImpacts.push(impactsObject);
      });

      const summaryValues = summaryImpacts?.reduce((acc: any, current: any) => {
        Object.entries(current).forEach(([key, value]) => {
          if (key.startsWith('impacts-')) {
            const numValue = Number(value);
            if (!Number.isNaN(numValue)) {
              acc[key] = (acc[key] || 0) + numValue;
            }
          }
        });

        return acc;
      }, {});

      const setIconData =
        (sustainability?.cartEnvironmentalItem?.icons as any[])
          ?.map((icon: any) => {
            const key = icon?.key;
            const value = summaryValues[key];

            if (typeof value !== 'number' || Number.isNaN(value)) return;

            return {
              icon: createElement(SVGIcon, {
                src: icon?.file?.fields?.file?.url || '',
                alt: icon?.title || '',
                imgClassName: 'max-w-full max-h-full',
                className: 'h-full w-full',
              }),
              text: String(value),
              units: icon?.unitOfMeasurement || '',
            };
          })
          .filter(Boolean) || [];

      return setIconData as IconText[];
    },
    [],
  );

  return { getImpactByProduct, getTotalImpactByCart };
};

export default useImpacts;

export interface UseImpactsReturn {
  getImpactByProduct: (
    variant: Variant,
    sustainability?: {
      cartEnvironmentalItem?: SustainabilityItem;
      certificationsItem?: SustainabilityItem;
      environmentalItem?: SustainabilityItem;
    },
  ) => IconText[];
  getTotalImpactByCart: (
    lineItems: LineItem[],
    sustainability?: {
      cartEnvironmentalItem?: SustainabilityItem;
      certificationsItem?: SustainabilityItem;
      environmentalItem?: SustainabilityItem;
    },
  ) => IconText[];
}
